/**
 * The ONE terrain sampling law, browser-safe (AD-408 §2; moved from
 * `backend/src/world/terrain-sample.ts`, not copied).
 *
 * Two things live here and nowhere else:
 *
 *   (i)  {@link decodeTerrainLayer} — base64 → little-endian `Int16Array`, a
 *        pure-TS table decoder. No `Buffer` (Node-only) and no `atob` (absent
 *        under older Node and a browser global the AD-122/AD-135 runtime-free
 *        rule keeps out of `shared/`).
 *   (ii) {@link resolveTerrainPixel} — world point → tile + intra-tile index,
 *        with the E3.1.S1 outer-edge clamp: "a query exactly on a set's outer max
 *        edge clamps inward". The clamp fires ONLY for a point exactly on a grid
 *        line whose forward pixel is uncommitted — never a nearest-tile search,
 *        which would silently invent readings for points outside the set.
 *
 * The backend placement sampler (band predicate, climate conversion) imports
 * both; the client ground surface (`frontend/src/game/scene/terrain.ts`) reads
 * pixel values through the same two functions. The backend's reading is the
 * NEAREST CELL and nothing here interpolates — the client's piecewise-linear
 * surface is a presentation law layered on top (AD-408 §3), agreeing with this
 * one exactly at pixel centres.
 *
 * Data boundary (AD-408, amending the E3.1.S1/S3 header sentences): the
 * ELEVATION raster ships in the client content pack (client-pack.ts sub-projects
 * `tiles[]` to `tx`/`tz`/`elevation`); the climate rasters (temperature,
 * humidity) and the wire (AD-125) stay untouched.
 */
import { worldUnitsToHeightmapPixel } from './terrain.js';
const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
/** Char code → 6-bit value; 255 = not in the standard alphabet. */
const BASE64_LOOKUP = (() => {
    const table = new Uint8Array(128).fill(255);
    for (let i = 0; i < BASE64_ALPHABET.length; i += 1)
        table[BASE64_ALPHABET.charCodeAt(i)] = i;
    return table;
})();
/** Decode standard (padded or unpadded) base64 into bytes; throws on a non-alphabet character. */
function decodeBase64(base64, label) {
    let end = base64.length;
    while (end > 0 && base64.charCodeAt(end - 1) === 61 /* '=' */)
        end -= 1;
    const out = new Uint8Array(Math.floor((end * 6) / 8));
    let buffer = 0;
    let bits = 0;
    let o = 0;
    for (let i = 0; i < end; i += 1) {
        const code = base64.charCodeAt(i);
        const value = code < 128 ? BASE64_LOOKUP[code] : 255;
        if (value === 255) {
            throw new Error(`terrain layer ${label}: invalid base64 character at offset ${i}`);
        }
        buffer = (buffer << 6) | value;
        bits += 6;
        if (bits >= 8) {
            bits -= 8;
            out[o] = (buffer >> bits) & 0xff;
            o += 1;
        }
        buffer &= (1 << bits) - 1;
    }
    return out;
}
/** Decode one base64 little-endian int16 raster into exactly `samples` values. */
export function decodeTerrainLayer(base64, samples, label) {
    const bytes = decodeBase64(base64, label);
    if (bytes.length !== samples * 2) {
        throw new Error(`terrain layer ${label}: expected ${samples * 2} bytes (${samples} int16 samples), decoded ${bytes.length}`);
    }
    const out = new Int16Array(samples);
    for (let i = 0; i < samples; i += 1) {
        const lo = bytes[i * 2];
        const hi = bytes[i * 2 + 1];
        const u = lo | (hi << 8);
        out[i] = u >= 0x8000 ? u - 0x10000 : u;
    }
    return out;
}
/**
 * Resolve a world point to a committed tile + intra-tile index (`hasTile` says
 * which tiles are committed), applying the outer-edge clamp (see header).
 * `null` when the point is off the set.
 */
export function resolveTerrainPixel(set, x, z, hasTile) {
    const ref = worldUnitsToHeightmapPixel(x, z, set);
    const onGridX = Number.isInteger((x - set.origin.x) / set.metersPerPixel);
    const onGridZ = Number.isInteger((z - set.origin.z) / set.metersPerPixel);
    const gpx = ref.tx * set.tileSizePx + ref.px;
    const gpz = ref.tz * set.tileSizePx + ref.pz;
    const candidates = [[gpx, gpz]];
    if (onGridX)
        candidates.push([gpx - 1, gpz]);
    if (onGridZ)
        candidates.push([gpx, gpz - 1]);
    if (onGridX && onGridZ)
        candidates.push([gpx - 1, gpz - 1]);
    for (const [cx, cz] of candidates) {
        const hit = resolveGlobalPixel(set, cx, cz, hasTile);
        if (hit)
            return hit;
    }
    return null;
}
/** Address a GLOBAL pixel (gpx, gpz) — no clamp; `null` when its tile is not committed. */
export function resolveGlobalPixel(set, gpx, gpz, hasTile) {
    const tx = Math.floor(gpx / set.tileSizePx);
    const tz = Math.floor(gpz / set.tileSizePx);
    if (!hasTile(tx, tz))
        return null;
    const px = gpx - tx * set.tileSizePx;
    const pz = gpz - tz * set.tileSizePx;
    return { tx, tz, index: pz * set.tileSizePx + px };
}
/**
 * Build a lazily-decoding reader over one layer of a tile set. A tile that is
 * committed but does not carry the requested layer throws on first touch — a
 * projection that dropped the layer must fail loudly, never read as "off-set".
 */
export function createTerrainLayerReader(set, layer) {
    const samples = set.tileSizePx * set.tileSizePx;
    const raw = new Map();
    for (const tile of set.tiles)
        raw.set(`${tile.tx},${tile.tz}`, tile);
    const decoded = new Map();
    const hasTile = (tx, tz) => raw.has(`${tx},${tz}`);
    const rasterOf = (tx, tz) => {
        const key = `${tx},${tz}`;
        const cached = decoded.get(key);
        if (cached)
            return cached;
        const encoded = raw.get(key)?.[layer];
        if (typeof encoded !== 'string') {
            throw new Error(`terrain set ${set.id} tile ${key}: layer '${layer}' is absent`);
        }
        const fresh = decodeTerrainLayer(encoded, samples, `${set.id} ${key} ${layer}`);
        decoded.set(key, fresh);
        return fresh;
    };
    const read = (hit) => hit ? rasterOf(hit.tx, hit.tz)[hit.index] : null;
    return {
        setId: set.id,
        layer,
        valueAt: (x, z) => read(resolveTerrainPixel(set, x, z, hasTile)),
        valueAtPixel: (gpx, gpz) => read(resolveGlobalPixel(set, gpx, gpz, hasTile)),
    };
}
/**
 * The backend's reading law as a one-shot query: floor-to-pixel nearest cell +
 * outer-edge clamp; `null` off-set. Uncached — use {@link createTerrainLayerReader}
 * for repeated queries.
 */
export function terrainPixelValue(set, layer, x, z) {
    return createTerrainLayerReader(set, layer).valueAt(x, z);
}
/**
 * Narrow an untyped client-pack `terrain-tiles` def into a readable set,
 * defensively (the `clientGenerationOf` pattern): a def of the wrong shape
 * yields `null` rather than a cast that lies, and the caller falls back to flat
 * ground.
 */
export function clientTerrainSetOf(def) {
    if (def === null || typeof def !== 'object')
        return null;
    const d = def;
    const origin = d['origin'];
    if (typeof d['id'] !== 'string' ||
        typeof d['metersPerPixel'] !== 'number' ||
        !(d['metersPerPixel'] > 0) ||
        typeof d['tileSizePx'] !== 'number' ||
        !Number.isInteger(d['tileSizePx']) ||
        !(d['tileSizePx'] > 0) ||
        origin === null ||
        typeof origin !== 'object' ||
        typeof origin['x'] !== 'number' ||
        typeof origin['z'] !== 'number' ||
        !Array.isArray(d['tiles'])) {
        return null;
    }
    const tiles = [];
    for (const entry of d['tiles']) {
        if (entry === null || typeof entry !== 'object')
            return null;
        const t = entry;
        if (!Number.isInteger(t['tx']) || !Number.isInteger(t['tz']))
            return null;
        if (typeof t['elevation'] !== 'string')
            return null;
        tiles.push({ tx: t['tx'], tz: t['tz'], elevation: t['elevation'] });
    }
    if (tiles.length === 0)
        return null;
    return {
        id: d['id'],
        metersPerPixel: d['metersPerPixel'],
        tileSizePx: d['tileSizePx'],
        origin: { x: origin['x'], z: origin['z'] },
        tiles,
    };
}
/** The `generation.terrain` set id a client-pack biome def binds, or `null`. */
export function clientTerrainIdOf(biomeDef) {
    if (biomeDef === null || typeof biomeDef !== 'object')
        return null;
    const generation = biomeDef['generation'];
    if (generation === null || typeof generation !== 'object')
        return null;
    const id = generation['terrain'];
    return typeof id === 'string' && id.length > 0 ? id : null;
}
//# sourceMappingURL=terrain-sample.js.map